import { SHELL_PORTAL } from 'lib/context/ShellContext/shellPortal';

/**
 * Single source of truth for "is the app currently rendering the admin practice
 * viewport, and for which practice". The axios interceptor, the navigation
 * rewriters and the route guard all read from here so they can never drift
 * apart — a page that does not get `practice_id` injected must not get its
 * links rewritten either.
 *
 * Legacy admin practice pages are excluded: they are the old profile screens,
 * not the viewport.
 */
const VIEWPORT_PATH_REGEX =
  /^\/admin\/practices\/(\d+)(?!\/(onboarding|form-builder|patient-import|facility|transactions|staff-management)(\/|$))(\/|$)/;

const PORTAL_PREFIX = '/portal';
const ADMIN_PREFIX = '/admin';

/**
 * The viewport the admin is working in right now.
 *
 * Deliberately in memory rather than sessionStorage: it only has to survive an
 * in-app navigation that leaves the viewport, and nothing more. A `/portal/...`
 * link from Slack, a bookmark or a notification email arrives on a fresh page
 * load with no memory, so the route guard cannot invent a practice for a URL
 * that never carried one — those keep the honest login bounce instead of
 * silently opening someone else's practice.
 */
let activeViewportPracticeId = null;

/**
 * @param {string} [pathname] - defaults to the live location
 * @returns {string|null} the viewport practice id, or null outside the viewport
 */
export const getViewportPracticeId = (pathname) => {
  if (pathname == null && typeof window === 'undefined') return null;
  const livePath = pathname ?? window.location.pathname;
  const match = VIEWPORT_PATH_REGEX.exec(livePath);

  // Only track the live location; explicit-pathname callers are just asking a
  // question about a string.
  if (pathname == null) {
    if (match) {
      activeViewportPracticeId = match[1];
    } else if (!livePath.startsWith(PORTAL_PREFIX)) {
      // Went off to do other admin work — the viewport is no longer the context
      // this admin is in, so stop offering it as an answer.
      activeViewportPracticeId = null;
    }
  }

  return match ? match[1] : null;
};

/**
 * The viewport practice for the current in-app context: the live URL if we are
 * still in the viewport, otherwise the one we navigated out of. Null once the
 * admin has moved on, and after any page load. Used by the `/portal/*` guard.
 *
 * @returns {string|null}
 */
export const getLastViewportPracticeId = () => getViewportPracticeId() || activeViewportPracticeId;

/**
 * Whether a target is an absolute portal path. A plain `startsWith('/portal/')`
 * misses bare `/portal` and `/portal?...` targets.
 *
 * @param {string} path
 * @returns {boolean}
 */
export const isPortalTarget = (path) =>
  path === PORTAL_PREFIX || /^\/portal[/?#]/.test(String(path ?? ''));

// Shell paths are relative to a shell's base and may keep their query: "beyond-billing/claims?x=1"
// in the practice portal; in the admin portal a practice's pages sit under "practices/3/".
export const withoutQuery = (path) => String(path || '').split('?')[0];

export const practicePathOf = (practiceId, path = '') =>
  path ? `practices/${practiceId}/${path}` : `practices/${practiceId}`;

export const viewportBaseOf = (practiceId) => `${ADMIN_PREFIX}/${practicePathOf(practiceId)}`;

/**
 * Rewrites an absolute `/portal/...` target onto the viewport base. Returns the
 * target untouched outside the viewport, so the practice portal is unaffected.
 *
 * @param {string} target
 * @param {string|null} practiceId
 * @returns {string}
 */
export const toViewportPath = (target, practiceId) => {
  if (!practiceId || typeof target !== 'string' || !isPortalTarget(target)) return target;
  return `${viewportBaseOf(practiceId)}${target.slice(PORTAL_PREFIX.length)}`;
};

export const shellBaseOf = (portal) =>
  portal === SHELL_PORTAL.ADMIN ? ADMIN_PREFIX : PORTAL_PREFIX;

export const pathAfter = (pathname, base) => {
  const path = String(pathname ?? '');
  const isUnder = path === base || path.startsWith(`${base}/`);
  return isUnder ? path.slice(base.length).replace(/^\/+/, '') : '';
};

export const firstSegmentAfter = (pathname, base) =>
  pathAfter(pathname, base).split('/').find(Boolean) || null;

const PRACTICE_SHELL_PATH = /^practices\/(\d+)(?:\/(.*))?$/;

export const splitShellPath = (shellPath) => {
  const path = withoutQuery(shellPath);
  const match = PRACTICE_SHELL_PATH.exec(path);
  return match
    ? { practiceId: Number(match[1]), path: match[2] || '' }
    : { practiceId: null, path };
};

export const practiceIdOf = (shellPath) => splitShellPath(shellPath).practiceId;

const CHART_PATH = /^charts\/(\d+)(?:\/([^/]*)|$)/;

export const chartOf = (shellPath) => {
  const match = CHART_PATH.exec(splitShellPath(shellPath).path);
  return match ? { chartId: Number(match[1]), section: match[2] || '' } : null;
};

export const chartIdOf = (shellPath) => chartOf(shellPath)?.chartId ?? null;

export const chartPathOf = (chartId, practiceId = null) =>
  practiceId ? practicePathOf(practiceId, `charts/${chartId}`) : `charts/${chartId}`;

export { PORTAL_PREFIX, VIEWPORT_PATH_REGEX };
